import {
  TWITTER_CONFIG,
  FACEBOOK_CONFIG,
  THREADS_CONFIG,
  INSTAGRAM_CONFIG,
  YOUTUBE_CONFIG,
  TIKTOK_CONFIG,
  SNAPCHAT_CONFIG,
  generateCodeVerifier,
  generateCodeChallenge,
} from './oauthUtils';

/**
 * Starting a social channel's sign-in (OAuth) in a popup, for a social_profiles
 * row that already exists. The popup's callback page posts "<PLATFORM>_AUTH_SUCCESS"
 * back to this window. Shared by Settings → Social and Reach → Channels.
 */

export async function initiateTwitterAuth(profileId: string) {
  try {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    sessionStorage.setItem('twitter_code_verifier', codeVerifier);
    sessionStorage.setItem('twitter_profile_id', profileId);

    const authParams = new URLSearchParams({
      response_type: 'code',
      client_id: TWITTER_CONFIG.clientId,
      redirect_uri: TWITTER_CONFIG.redirectUri,
      scope: TWITTER_CONFIG.scope,
      state: `profile_${profileId}_${Date.now()}`,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    } as Record<string, string>);

    const authUrl = `${TWITTER_CONFIG.authUrl}?${authParams.toString()}`;

    const authWindow = window.open(
      authUrl,
      'twitter_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
      }
    }, 1000);
  } catch (error) {
    console.error('Error initiating Twitter OAuth:', error);
  }
}

export async function initiateFacebookAuth(profileId: string) {
  try {
    sessionStorage.setItem('facebook_profile_id', profileId);

    const state = `profile_${profileId}_${Date.now()}`;
    sessionStorage.setItem('facebook_state', state);

    const authParams = new URLSearchParams({
      client_id: FACEBOOK_CONFIG.clientId,
      redirect_uri: FACEBOOK_CONFIG.redirectUri,
      scope: FACEBOOK_CONFIG.scope,
      response_type: 'code',
      state: state,
    } as Record<string, string>);

    const authUrl = `${FACEBOOK_CONFIG.authUrl}?${authParams.toString()}`;

    console.log('Initiating Facebook OAuth flow for profile:', profileId);
    console.log('Authorization URL:', authUrl);

    const authWindow = window.open(
      authUrl,
      'facebook_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
      }
    }, 1000);
  } catch (error) {
    console.error('Error initiating Facebook OAuth:', error);
  }
}

export async function initiateThreadsAuth(profileId: string) {
  try {
    sessionStorage.setItem('threads_profile_id', profileId);

    const state = `profile_${profileId}_${Date.now()}`;
    sessionStorage.setItem('threads_state', state);

    const authParams = new URLSearchParams({
      client_id: THREADS_CONFIG.clientId,
      redirect_uri: THREADS_CONFIG.redirectUri,
      scope: THREADS_CONFIG.scope,
      response_type: 'code',
      state: state,
    } as Record<string, string>);

    const authUrl = `${THREADS_CONFIG.authUrl}?${authParams.toString()}`;

    console.log('Initiating Threads OAuth flow for profile:', profileId);
    console.log('Authorization URL:', authUrl);

    const authWindow = window.open(
      authUrl,
      'threads_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
      }
    }, 1000);
  } catch (error) {
    console.error('Error initiating Threads OAuth:', error);
  }
}

export async function initiateInstagramAuth(profileId: string) {
  try {
    sessionStorage.setItem('instagram_profile_id', profileId);

    const state = `profile_${profileId}_${Date.now()}`;
    sessionStorage.setItem('instagram_state', state);

    const authParams = new URLSearchParams({
      client_id: INSTAGRAM_CONFIG.clientId,
      redirect_uri: INSTAGRAM_CONFIG.redirectUri,
      scope: INSTAGRAM_CONFIG.scope,
      response_type: 'code',
      state: state,
    } as Record<string, string>);

    const authUrl = `${INSTAGRAM_CONFIG.authUrl}?${authParams.toString()}`;

    console.log('Initiating Instagram OAuth flow for profile:', profileId);
    console.log('Authorization URL:', authUrl);

    const authWindow = window.open(
      authUrl,
      'instagram_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
      }
    }, 1000);
  } catch (error) {
    console.error('Error initiating Instagram OAuth:', error);
  }
}

export async function initiateYouTubeAuth(profileId: string) {
  try {
    sessionStorage.setItem('youtube_profile_id', profileId);

    const state = `profile_${profileId}_${Date.now()}`;
    sessionStorage.setItem('youtube_state', state);

    const authParams = new URLSearchParams({
      client_id: YOUTUBE_CONFIG.clientId,
      redirect_uri: YOUTUBE_CONFIG.redirectUri,
      scope: YOUTUBE_CONFIG.scope,
      response_type: 'code',
      state: state,
      access_type: 'offline',
      prompt: 'consent',
    } as Record<string, string>);

    const authUrl = `${YOUTUBE_CONFIG.authUrl}?${authParams.toString()}`;

    console.log('Initiating YouTube OAuth flow for profile:', profileId);
    console.log('Authorization URL:', authUrl);

    const authWindow = window.open(
      authUrl,
      'youtube_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
      }
    }, 1000);
  } catch (error) {
    console.error('Error initiating YouTube OAuth:', error);
  }
}

export async function initiateTikTokAuth(profileId: string) {
  try {
    sessionStorage.setItem('tiktok_profile_id', profileId);

    const state = `profile_${profileId}_${Date.now()}`;
    sessionStorage.setItem('tiktok_state', state);

    const authParams = new URLSearchParams({
      client_key: TIKTOK_CONFIG.clientKey,
      redirect_uri: TIKTOK_CONFIG.redirectUri,
      scope: TIKTOK_CONFIG.scope,
      response_type: 'code',
      state: state,
    } as Record<string, string>);

    const authUrl = `${TIKTOK_CONFIG.authUrl}?${authParams.toString()}`;

    console.log('Initiating TikTok OAuth flow for profile:', profileId);
    console.log('Authorization URL:', authUrl);

    const authWindow = window.open(
      authUrl,
      'tiktok_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
      }
    }, 1000);
  } catch (error) {
    console.error('Error initiating TikTok OAuth:', error);
  }
}

export async function initiateSnapchatAuth(profileId: string) {
  try {
    sessionStorage.setItem('snapchat_profile_id', profileId);

    const state = `profile_${profileId}_${Date.now()}`;
    sessionStorage.setItem('snapchat_state', state);

    const authParams = new URLSearchParams({
      client_id: SNAPCHAT_CONFIG.clientId,
      redirect_uri: SNAPCHAT_CONFIG.redirectUri,
      scope: SNAPCHAT_CONFIG.scope,
      response_type: 'code',
      state: state,
    } as Record<string, string>);

    const authUrl = `${SNAPCHAT_CONFIG.authUrl}?${authParams.toString()}`;

    console.log('Initiating Snapchat OAuth flow for profile:', profileId);
    console.log('Authorization URL:', authUrl);

    const authWindow = window.open(
      authUrl,
      'snapchat_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
      }
    }, 1000);
  } catch (error) {
    console.error('Error initiating Snapchat OAuth:', error);
  }
}

/** Start the sign-in for a channel of this platform. Platforms without one resolve at once. */
export async function startChannelAuth(platform: string, profileId: string) {
  switch (platform.toLowerCase()) {
    case 'x':
    case 'twitter':
      return initiateTwitterAuth(profileId);
    case 'facebook':
      return initiateFacebookAuth(profileId);
    case 'threads':
      return initiateThreadsAuth(profileId);
    case 'instagram':
      return initiateInstagramAuth(profileId);
    case 'youtube':
      return initiateYouTubeAuth(profileId);
    case 'tiktok':
      return initiateTikTokAuth(profileId);
    case 'snapchat':
      return initiateSnapchatAuth(profileId);
    default:
      return undefined;
  }
}

/** The popup callbacks' success messages. */
export const CHANNEL_AUTH_SUCCESS = new Set([
  'TWITTER_AUTH_SUCCESS',
  'FACEBOOK_AUTH_SUCCESS',
  'THREADS_AUTH_SUCCESS',
  'INSTAGRAM_AUTH_SUCCESS',
  'YOUTUBE_AUTH_SUCCESS',
  'TIKTOK_AUTH_SUCCESS',
  'SNAPCHAT_AUTH_SUCCESS',
]);
